import { useCallback, useMemo, useState } from "react";
import Addicon from "@mui/icons-material/Add";
import TimeLineIcon from "remixicon-react/TimeLineIcon";
import TruckLineIcon from "remixicon-react/TruckLineIcon";
import { useNavigate, useLocation } from "react-router-dom";
import { createUseStyles } from "$utils/createUseStyles";
import EdbPopin from "$containers/EDB/EdbPopin";
import { publicIconPath } from "$utils/constants";
import { getEdbStatusColor, getEdbStatusLabel } from "$utils/typeConstants";
import { DispositionType, EdbStatus, PreparationOrderStatus } from "$types";
import { Text, Button, InfoContainer, Badge } from "$components";
import JTable from "$components/JTable/JTable";
import { EMPTY_DATA } from "$containers/constants";
import useAuth from "$hooks/useAuth";
import { ImportButtonWithModal } from "$components/Forms/ImportButtonWithModal";
import { ImportType } from "$types/import/ImportType";
import { theme } from "$utils/theme/bataillog";

type Props = {
  dockyard: any;
  edbs: any[];
  handleEdbRedirect: () => void;
  dockyardId: string;
};

function EDBs({ dockyard, edbs, handleEdbRedirect, dockyardId }: Props) {
  const { isAdmin, isContributor, isClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();

  const [edbPopin, setEdbPopin] = useState<any>(null);
  const [indexEdb, setIndexEdb] = useState(0);

  const buildEdbBreadcrumbs = useCallback(
    (edb: any) => {
      const existing = (
        location.state as
          { breadcrumbs?: Array<{ path: string; name: string }> } | undefined
      )?.breadcrumbs;
      if (existing?.length) {
        return [...existing, { path: `/edbs/${edb._id}`, name: edb.edbId }];
      }
      const { pathname } = location;
      const parts = pathname.split("/").filter(Boolean);
      const chain: Array<{ path: string; name: string }> = [];
      if (pathname.startsWith("/chantiers/")) {
        chain.push({ path: "/chantiers", name: "Liste des chantiers" });
        if (dockyard?.name) chain.push({ path: pathname, name: dockyard.name });
      } else if (pathname.startsWith("/projets/")) {
        chain.push({ path: "/projets", name: "Liste des projets" });
        const idProject = parts[1];
        const projectName = dockyard?.project?.name || dockyard?.project?.code;
        if (idProject && projectName) {
          chain.push({ path: `/projets/${idProject}`, name: projectName });
        }
        if (dockyard?.name) chain.push({ path: pathname, name: dockyard.name });
      }
      chain.push({ path: `/edbs/${edb._id}`, name: edb.edbId });
      return chain;
    },
    [dockyard, location]
  );

  const onEdbClickAction = useCallback(
    (edb: any) => {
      if (edb.status === EdbStatus.CART_CREATED) {
        navigate(`/create-edb/${edb._id}`);
      } else {
        navigate(`/edbs/${edb._id}`, {
          state: { breadcrumbs: buildEdbBreadcrumbs(edb) },
        });
      }
    },
    [buildEdbBreadcrumbs, navigate]
  );

  const resetEdbPopin = () => {
    setEdbPopin(null);
  };

  const data = useMemo(
    () =>
      edbs.map((edb: any) => {
        const dispatched = edb?.ops?.filter(
          (op: any) =>
            op.status === PreparationOrderStatus.SHIPPED ||
            op.status === PreparationOrderStatus.DELIVERED ||
            (op.status === PreparationOrderStatus.COLLECTED &&
              op.shipType !== DispositionType.COLLECTED)
        );
        const isEdbDisabled =
          edb.isDisable ||
          edb.dockyardDisable ||
          edb.projectDisable ||
          edb.companyDisable;

        return {
          ...edb,
          dispatchedCount: dispatched?.length || 0,
          isEdbDisabled,
        };
      }),
    [edbs]
  );

  const columns = useMemo(
    () => [
      {
        id: "edbNumber",
        Header: "Identification",
        Cell: ({ row }: any) => (
          <div
            className={
              row.original.isEdbDisabled ? classes.cellContentDisabled : ""
            }
          >
            <Text type="important">{row.original.edbId || ""}</Text>
            <br />
            <Text style={{ width: 150 }}>
              {row.original.company?.name || ""}
            </Text>
          </div>
        ),
        meta: { width: 150 },
      },
      {
        id: "project",
        Header: "Projet",
        Cell: ({ row }: any) => (
          <div
            className={
              row.original.isEdbDisabled ? classes.cellContentDisabled : ""
            }
          >
            <Text type="important">{row.original?.project?.code || ""}</Text>
            <br />
            <Text style={{ width: 150 }}>
              {row.original?.project?.name || ""}
            </Text>
          </div>
        ),
        meta: { width: 150 },
      },
      {
        id: "qtyPrep",
        Header: (
          <div>
            <TimeLineIcon
              color={theme.color.blue}
              size={15}
              style={{ marginRight: 5 }}
            />
            Préparation
          </div>
        ),
        Cell: ({ row }: any) => (
          <span className={classes.text}>{`${row.original?.ops?.length || 0} ${
            (row.original?.ops?.length || 0) > 1
              ? "préparations"
              : "préparation"
          }`}</span>
        ),
        meta: { width: 150 },
      },
      {
        id: "qtyExpedition",
        Header: (
          <div>
            <TruckLineIcon
              color={theme.color.blue}
              size={15}
              style={{ marginRight: 5 }}
            />
            Expédition
          </div>
        ),
        Cell: ({ row }: any) => (
          <span className={classes.text}>{`${row.original.dispatchedCount} ${
            row.original.dispatchedCount > 1 ? "expéditions" : "expédition"
          }`}</span>
        ),
        meta: { width: 150 },
      },
      {
        id: "refPreparationEntrepot",
        Header: "Ref OP, prep. entrepôt",
        Cell: ({ row }: any) => (
          <span>
            {row.original.ops?.length
              ? row.original.ops?.map((op: any, opIndex: number) => (
                  <div key={op?.reference ?? opIndex}>
                    <Text type="important">{op?.reference}</Text>
                    <br />
                    <Text>{op?.refPreparationEntrepot || EMPTY_DATA}</Text>
                  </div>
                ))
              : "Pas de référence"}
          </span>
        ),
        meta: { width: 150 },
      },
      {
        id: "status",
        Header: "Statut",
        Cell: ({ row }: any) => (
          <div style={{ whiteSpace: "nowrap", margin: "4px 0" }}>
            {row.original.status === EdbStatus.CART_CREATED ? (
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdbClickAction(row.original);
                }}
                style={{
                  padding: "7px",
                  fontSize: 13,
                  minWidth: 130,
                  display: "block",
                  lineHeight: "15px",
                }}
              >
                Brouillon
              </Button>
            ) : row.original.preparationType === "Préparation & restockage" ? (
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  setEdbPopin(row.original);
                  setIndexEdb(row.index);
                }}
                style={{
                  padding: "7px",
                  fontSize: 13,
                  minWidth: 130,
                  display: "block",
                  lineHeight: "15px",
                }}
              >
                Donner <br />
                les instructions
              </Button>
            ) : (
              <Badge
                colorManual={getEdbStatusColor(
                  row.original.status,
                  theme,
                  row.original.isDisable
                )}
              >
                <span
                  style={{
                    width: 124,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    overflow: "hidden",
                    fontFamily: "Roboto",
                    fontSize: 13,
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  {getEdbStatusLabel(row.original.status)}
                </span>
              </Badge>
            )}
          </div>
        ),
        meta: { width: 100 },
      },
      {
        id: "annule",
        Header: "Annulé",
        Cell: ({ row }: any) => (
          <span className={classes.text}>
            {row.original.isDisable ? "OUI" : "NON"}
          </span>
        ),
        meta: { width: 80 },
      },
    ],
    [classes, onEdbClickAction]
  );

  const disableAddEDB =
    dockyard?.isDisable ||
    dockyard?.projectDisable ||
    dockyard?.companyDisable ||
    dockyard?.status === "done" ||
    (!isAdmin() && !isClient() && !isContributor());

  return (
    <>
      <InfoContainer
        bodyClassName={classes.EdbBodyTable}
        className={classes.edbContainer}
        leftHeader={
          <div className={classes.infoHeader}>
            <img alt="ico-batail" src={`${publicIconPath}/casque-black.svg`} />
            <Text type="h3">Expressions de besoin</Text>
          </div>
        }
        rightHeader={
          <>
            <Button
              className={disableAddEDB ? classes.CtaDone : ""}
              onClick={handleEdbRedirect}
              variant="outlined"
              disabled={disableAddEDB}
            >
              <Addicon />
              Ajouter une EDB
            </Button>
            {!isContributor() && (
              <ImportButtonWithModal
                disabled={disableAddEDB}
                title="Opérations en masse"
                dataName="EDB du chantier"
                importType={ImportType.DOCKYARD_EDB}
                buttonTexts={{
                  importButtonText: "Importer des EDB",
                  updateButtonText: "Mettre à jour des EDB",
                }}
                contextId={dockyardId}
                isFeminine
              />
            )}
          </>
        }
      >
        <JTable
          data={data}
          columns={columns}
          setPageIndex={() => {}}
          hideCheckboxes
          hideFilters
          rowsPerPage={8}
          noDataText={
            !edbs.length
              ? "Aucune expression de besoin n'a encore été ajoutée à ce chantier"
              : undefined
          }
          usePaper={false}
          rowProps={(row) => ({
            onClick: () => {
              if (
                row.original.preparationType !== "Préparation & restockage" ||
                row.original.status !== "prep_avalaible"
              ) {
                onEdbClickAction(row.original);
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </InfoContainer>
      <EdbPopin edb={edbPopin} resetEdb={resetEdbPopin} indexEdb={indexEdb} />
    </>
  );
}

export default EDBs;

const useStyles = createUseStyles(() => ({
  EdbBodyTable: {
    padding: 0,
  },
  text: {
    marginLeft: 5,
  },
  cellContentDisabled: {
    color: "#B3BAC5 !important",
    "& *": {
      color: "#B3BAC5 !important",
    },
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: "0 8px",
    },
  },

  infoContainer: {
    marginTop: 10,
    marginBottom: 20,
  },
  CtaDone: {
    color: `${theme.color.gray3} !important`,
    pointerEvents: "none",
    "&:hover": {
      color: `${theme.color.gray3} !important`,
    },
  },
  edbContainer: {
    marginTop: 10,
  },
}));
